import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Button, StatusChip } from "@/components/ui";
import { useApiClient } from "@/api/useApiClient";
import { HubTabs, LoadBoundary, Panel, useHubLoad } from "@/modules/hub-kit/HubKit";
import { formatCount, formatDateTime } from "@/modules/hub-kit/format";
import { createFinanceOverviewApi, type IntegrityCheck } from "../overviewApi";
import { ExceptionsQueuePage } from "../pages/FinanceQueuePages";

/** Where the sample ids of a failed check can be opened. */
function linkFor(entity: string, id: string): string | null {
  switch (entity) {
    case "transaction":
      return `/app/finance/transactions/${id}`;
    case "payout":
      return `/app/finance/payouts/${id}`;
    case "settlement":
      return `/app/finance/settlements/${id}`;
    case "integration_event":
      return `/app/system/integration-events/${id}`;
    default:
      return null;
  }
}

function severityTone(c: IntegrityCheck): "danger" | "warning" | "neutral" {
  if (c.count === 0) return "neutral";
  return c.severity === "critical" ? "danger" : "warning";
}

function LedgerChecks() {
  const client = useApiClient();
  const api = useMemo(() => createFinanceOverviewApi(client), [client]);
  const navigate = useNavigate();
  const load = useCallback(() => api.getIntegrityChecks(), [api]);
  const { data, error, loading, reload } = useHubLoad(load);

  return (
    <LoadBoundary
      loading={loading}
      error={error}
      onRetry={() => void reload()}
      hasData={!!data}
      what="the ledger checks"
    >
      {data ? (
        <Stack gap={16}>
          <Flex gap={12} align="center" wrap justify="space-between">
            <Text role="body" color="secondary" as="p">
              {data.violations === 0
                ? `All ${data.checks.length} checks passed.`
                : `${formatCount(data.violations)} record${data.violations === 1 ? "" : "s"} across ${data.checks.filter((c) => c.count > 0).length} check${data.checks.filter((c) => c.count > 0).length === 1 ? "" : "s"} need a look.`}{" "}
              Run {formatDateTime(data.checked_at)}, straight from the ledger tables.
            </Text>
            <Button variant="secondary" onClick={() => void reload()}>
              Run again
            </Button>
          </Flex>
          <Stack gap={8}>
            {data.checks.map((c) => (
              <div
                key={c.code}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "var(--space-12)",
                  padding: "var(--space-12)",
                  border: "1px solid var(--color-border-subtle)",
                  borderRadius: "var(--radius-sm)",
                  background: "var(--color-surface-raised)",
                }}
              >
                <Stack gap={4}>
                  <Text role="bodyStrong" as="span">
                    {c.title}
                  </Text>
                  <Text role="small" color="secondary" as="span">
                    {c.description}
                  </Text>
                  {c.count > 0 && c.sample_ids.length > 0 ? (
                    <Flex gap={8} wrap>
                      {c.sample_ids.map((id) => {
                        const to = linkFor(c.entity, id);
                        return to ? (
                          <Button
                            key={id}
                            variant="ghost"
                            size="sm"
                            onClick={() => void navigate(to)}
                          >
                            Open {id.slice(0, 8)}
                          </Button>
                        ) : (
                          <Text key={id} role="caption" color="muted" as="span">
                            {id}
                          </Text>
                        );
                      })}
                      {c.count > c.sample_ids.length ? (
                        <Text role="caption" color="muted" as="span">
                          and {c.count - c.sample_ids.length} more
                        </Text>
                      ) : null}
                    </Flex>
                  ) : null}
                </Stack>
                <div style={{ whiteSpace: "nowrap" }}>
                  <StatusChip tone={c.count === 0 ? "success" : severityTone(c) === "danger" ? "danger" : "warning"}>
                    {c.count === 0 ? "Clear" : `${formatCount(c.count)} found`}
                  </StatusChip>
                </div>
              </div>
            ))}
          </Stack>
        </Stack>
      ) : null}
    </LoadBoundary>
  );
}

export function FinanceReconciliationTab() {
  return (
    <Stack gap={16}>
      <Panel
        title="How reconciliation works here"
        description="Xendit confirms a payment by webhook; the ledger records it; these checks compare what the ledger says against itself and against the bookings, refunds, payouts and settlements around it. A failed webhook, a payment with no booking, a refund larger than its payment or a payout that does not add up shows here and in the exceptions queue."
      >
        <Text role="small" color="secondary" as="p">
          These checks are read-only. Fixing a problem is a new, audited record (an adjustment, a refund or a resolved exception), never an edit to a finalized amount.
        </Text>
      </Panel>
      <HubTabs
        param="sub"
        variant="sub"
        items={[
          { id: "checks", label: "Ledger checks", content: <LedgerChecks /> },
          { id: "exceptions", label: "Exceptions", content: <ExceptionsQueuePage /> },
        ]}
      />
    </Stack>
  );
}
